import React from "react";
import { GroupBase, SelectInstance, StylesConfig } from "react-select";
import AsyncSelect from "react-select/async";
import { Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import styles from "@/components/subjectSearch/subjectSearchSelect.module.css";
import { subjectSearchSelectStyles } from "@/components/subjectSearch/subjectSearchSelectStyles";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { MandatoryMarker } from "@/components/forms/inputs/mandatoryMarker";
import type { QuickSearchSelectionMetadata } from "@/types/QuickSearch";
import {
  filterPreloadedDropDownOptions,
  useDropDownOptionPreload,
} from "@/helper/customHooks/useDropDownOptionPreload";
import DropDownOptionPreloadRow from "@/components/forms/inputs/dropDownOptionPreloadRow";
import type { IDropDownOption } from "@/types/DropDown";

interface SubjectSearchSelectProps {
  name?: string;
  value?: any;
  loadOptions?: (inputValue: string) => Promise<IDropDownOption[]>;
  cacheOptions?: boolean;
  onChange?: (value: any) => void;
  inputValue?: string;
  onInputChange?: (inputValue: string) => void;
  showOptionsMenu?: boolean;
  placeholder?: React.ReactNode;
  isMulti?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  error?: any;
  mandatory?: boolean;
  autofocus?: boolean;
  customStyles?: StylesConfig<any, any, GroupBase<any>> | undefined;
  classNamePrefix?: string;
  additionalWrapperStyles?: string | any;
  lookLikeTextField?: boolean;
  debounceMs?: number;
  getSelectionBlockedMessage?: (
    selection: QuickSearchSelectionMetadata,
  ) => React.ReactNode;
  loadAllOptions?: () => Promise<IDropDownOption[]>;
  loadAllOptionsResetKey?: unknown;
  onLoadAllOptionsError?: (error: unknown) => void;
}

export const SubjectSearchSelect = React.forwardRef<
  SelectInstance,
  SubjectSearchSelectProps
>(function SubjectSearchSelect(props, ref) {
  const {
    name,
    value,
    loadOptions,
    cacheOptions = true,
    onChange,
    inputValue,
    onInputChange,
    showOptionsMenu,
    placeholder,
    isMulti,
    isClearable,
    isDisabled,
    error,
    mandatory,
    autofocus,
    customStyles,
    classNamePrefix,
    additionalWrapperStyles,
    lookLikeTextField,
    debounceMs = 500,
    getSelectionBlockedMessage,
    loadAllOptions,
    loadAllOptionsResetKey,
    onLoadAllOptionsError,
  } = props;

  function HiddenDropdownIndicator() {
    return null;
  }

  function HiddenIndicatorSeparator() {
    return null;
  }

  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("search_placeholder");

  const debounce = React.useCallback((fn: any, delay = 250) => {
    let timeout: any;
    return (...args: any) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  }, []);

  const loadOptionsDebounced = React.useMemo(() => {
    return debounce((inputValue: string, callback: any) => {
      if (!loadOptions) return callback([]);
      loadOptions(inputValue)
        .then((opts) => callback(opts))
        .catch(() => callback([]));
    }, debounceMs);
  }, [debounce, debounceMs, loadOptions]);

  const effectiveLoadAllOptions =
    showOptionsMenu === false ? undefined : loadAllOptions;
  const {
    defaultOptions,
    forceMenuOpen,
    handlePreload,
    hasPreloadedOptions,
    isPreloading,
    preloadedOptions,
    onMenuClose,
    openMenu,
  } = useDropDownOptionPreload<IDropDownOption>({
    disabled: isDisabled,
    loadAllOptions: effectiveLoadAllOptions,
    resetKey: loadAllOptionsResetKey,
    fallbackDefaultOptions: true,
    onLoadError: onLoadAllOptionsError,
  });

  const selectRef = React.useRef<SelectInstance | null>(null);

  const setCombinedRef = (instance: SelectInstance | null) => {
    selectRef.current = instance;

    if (typeof ref === "function") {
      ref(instance);
    } else if (ref) {
      ref.current = instance;
    }
  };

  const handleLoadOptions = (
    inputValue: string,
    callback: (options: IDropDownOption[]) => void,
  ) => {
    if (preloadedOptions !== null) {
      callback(filterPreloadedDropDownOptions(preloadedOptions, inputValue));
      return;
    }
    loadOptionsDebounced(inputValue, callback);
  };

  const handlePreloadClick = async () => {
    selectRef.current?.focus?.();
    const shouldOpenMenu = await handlePreload();
    const select = selectRef.current;

    if (shouldOpenMenu && select?.inputRef === document.activeElement) {
      openMenu();
    }
  };

  const ClearIndicatorForTextField = (indicatorProps: any) => (
    <div
      ref={indicatorProps.innerRef}
      {...indicatorProps.innerProps}
      style={indicatorProps.getStyles("clearIndicator", indicatorProps)}
    >
      <Tooltip title={t("reset_filter")} placement="right">
        <ClearIcon
          sx={{
            color: "rgba(0, 0, 0, 0.4)",
            fontSize: "1.25rem",
            display: "block",
            "&:hover": {
              color: "rgba(0, 0, 0, 0.8)",
            },
          }}
        />
      </Tooltip>
    </div>
  );

  const isInlineSearchMode = showOptionsMenu === false;
  const shouldExposeClearIndicatorForInput =
    isInlineSearchMode &&
    typeof inputValue === "string" &&
    inputValue.length > 0 &&
    isClearable;

  const effectiveValue = shouldExposeClearIndicatorForInput
    ? { value: inputValue, label: inputValue }
    : value;

  const selectComponentOverrides = (() => {
    const components: Record<string, any> = {};

    if (lookLikeTextField) {
      components.ClearIndicator = ClearIndicatorForTextField;
    }

    if (isInlineSearchMode) {
      components.DropdownIndicator = HiddenDropdownIndicator;
      components.IndicatorSeparator = HiddenIndicatorSeparator;
    }

    return Object.keys(components).length > 0 ? components : undefined;
  })();

  return (
    <div
      className={classNames(styles.subjectSearchWrapper, {
        [additionalWrapperStyles]: additionalWrapperStyles,
      })}
    >
      <DropDownOptionPreloadRow
        disabled={isDisabled}
        hasLoadedOptions={hasPreloadedOptions}
        isLoading={isPreloading}
        onBlur={onMenuClose}
        onPreload={effectiveLoadAllOptions ? handlePreloadClick : undefined}
      >
        <AsyncSelect
          classNamePrefix={classNamePrefix}
          ref={setCombinedRef}
          inputId={name}
          cacheOptions={preloadedOptions !== null ? false : cacheOptions}
          defaultOptions={defaultOptions}
          isLoading={isPreloading || undefined}
          aria-label={name ? undefined : "DropDown-Async-Select"}
          loadOptions={handleLoadOptions}
          loadingMessage={() => t("loading")}
          noOptionsMessage={({ inputValue }) =>
            inputValue.trim().length > 0
              ? t("no_options_found")
              : t("type_to_search_placeholder")
          }
          value={effectiveValue}
          onChange={(newValue: any, actionMeta: any) => {
            const selectedValues = Array.isArray(newValue)
              ? newValue
              : newValue
                ? [newValue]
                : [];
            if (
              getSelectionBlockedMessage &&
              selectedValues.some(
                (option) => option.selection?.selectable === false,
              )
            ) {
              return;
            }
            onChange?.(newValue);
            if (
              isInlineSearchMode &&
              actionMeta?.action === "clear" &&
              onInputChange
            ) {
              onInputChange("");
            }
          }}
          inputValue={inputValue}
          onInputChange={(newValue: string, actionMeta: any) => {
            if (actionMeta?.action === "input-change") {
              onInputChange?.(newValue);
              return newValue;
            }
            if (actionMeta?.action === "set-value") {
              return "";
            }
            if (
              isInlineSearchMode &&
              (actionMeta?.action === "input-blur" ||
                actionMeta?.action === "menu-close")
            ) {
              return inputValue ?? "";
            }
            return newValue;
          }}
          menuIsOpen={
            showOptionsMenu !== undefined
              ? showOptionsMenu
              : hasPreloadedOptions
                ? forceMenuOpen
                : undefined
          }
          onMenuClose={onMenuClose}
          onMenuOpen={
            showOptionsMenu === undefined && hasPreloadedOptions
              ? openMenu
              : undefined
          }
          controlShouldRenderValue={!isInlineSearchMode}
          isMulti={isMulti}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isOptionDisabled={(option: any) =>
            Boolean(getSelectionBlockedMessage) &&
            option.selection?.selectable === false
          }
          placeholder={
            mandatory ? (
              <>
                {resolvedPlaceholder}
                <MandatoryMarker />
              </>
            ) : (
              resolvedPlaceholder
            )
          }
          formatOptionLabel={(option: any) => {
            const classes = [] as string[];
            if (option.is_parent) classes.push(styles.parentCompany);
            if (option.is_deleted === true) classes.push(styles.deletedUser);
            if (option.is_active === false) classes.push(styles.inactiveCompany);

            const isSelectionBlocked =
              Boolean(getSelectionBlockedMessage) &&
              option.selection?.selectable === false;
            if (isSelectionBlocked) {
              classes.push(styles.selectionBlockedLabel);
            }
            const blockedMessage =
              isSelectionBlocked && getSelectionBlockedMessage
                ? getSelectionBlockedMessage(option.selection)
                : null;

            if (classes.length > 0 || blockedMessage) {
              return (
                <span className={styles.optionContent}>
                  <span className={classes.join(" ")}>{option.label}</span>
                  {blockedMessage && (
                    <span className={styles.selectionBlockedReason}>
                      {blockedMessage}
                    </span>
                  )}
                </span>
              );
            }
            return option.label;
          }}
          autoFocus={autofocus}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          styles={subjectSearchSelectStyles(
            error,
            customStyles,
            lookLikeTextField,
          )}
          components={selectComponentOverrides}
        />
      </DropDownOptionPreloadRow>
    </div>
  );
});

/*
Usage:
- SubjectSearchSelect import as usual - this is the Select Component
- onChange response is selectedOption as defined for user/company {} or [] single/multi 
- also import the useSubjectSearchHook - import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
-  

hookUsage:
 const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
  });
- for singleUsage in a component you can define all parameters within the hookDefinition itself
- for multipleUsage/ multiple subjectSearchSelects in one component
  - define common parameters in hooks definition
  - onChange takes overrides
    -  loadOptions={(input: any) =>
                fetchSubjects(input, { queryParameter: "companies" })
              }
- parameter returnRawData returns res.data and skips user/company stuff


example/used (multi) in appointmentList.tsx

*/
